import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus, Film, Trash2, Sparkles, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { TopNav } from "@/components/TopNav";
import { BrandKitDialog } from "@/components/BrandKitDialog";
import { MentionInbox } from "@/components/MentionInbox";
import { api, formatApiErrorDetail } from "@/lib/apiClient";
import { toast } from "sonner";

const STATUS_COLORS = {
  draft: "text-white/50 border-white/15",
  uploaded: "text-[#00E5FF] border-[#00E5FF]/40",
  analyzed: "text-[#39FF14] border-[#39FF14]/40",
  enhanced: "text-[#FF007F] border-[#FF007F]/40",
  ready: "text-[#39FF14] border-[#39FF14]/40",
};

const GENRES = ["Auto", "Story / Vlog", "Comedy", "Motivational", "Product / Ad", "Music", "Educational", "Cinematic Short"];

const STORY_TEMPLATES = [
  { key: "fairy_tale", label: "Fairy Tale", genre: "Cinematic Short",
    title: "The Whispering Forest",
    description: "A curious child discovers a hidden forest where the trees tell stories.",
    story_prompt: "A warm, magical fairy tale for all ages. A brave young hero enters an enchanted forest, meets a wise talking creature, overcomes a gentle challenge, and learns a heartfelt lesson. Dreamlike, storybook visuals with soft golden light." },
  { key: "hype_ad", label: "Hype Ad", genre: "Product / Ad",
    title: "Drop Day",
    description: "A high-energy launch teaser for a bold new product.",
    story_prompt: "A punchy 20-30s hype ad. Fast cuts, dramatic reveals, confident energy, bold on-brand mood. Build anticipation, show the product as the hero, end on a powerful call-to-action moment. Cinematic, modern, premium." },
  { key: "explainer", label: "Explainer", genre: "Educational",
    title: "How It Works",
    description: "A clear, friendly explainer that breaks down a big idea in simple steps.",
    story_prompt: "A calm, friendly explainer. Introduce a relatable problem, walk through the idea in 3 simple steps with clean visual metaphors, and end with an encouraging takeaway. Bright, clear, professional." },
  { key: "hero_journey", label: "Hero's Journey", genre: "Cinematic Short",
    title: "Against the Odds",
    description: "An underdog rises, faces their fear, and triumphs.",
    story_prompt: "An inspiring mini hero's journey. Ordinary beginning, a call to adventure, a moment of doubt, a turning point, and a triumphant finish. Epic cinematic grade, emotional swell, uplifting resolution." },
  { key: "travel", label: "Travel Reel", genre: "Story / Vlog",
    title: "Somewhere New",
    description: "A dreamy travel reel through a stunning destination.",
    story_prompt: "A dreamy travel montage. Sweeping establishing shots, small human moments, golden-hour light, a sense of wonder and wanderlust. Smooth, cinematic, immersive." },
];
const ENGINES = [
  { value: "openai", label: "OpenAI · GPT-5.4 + GPT Image" },
  { value: "gemini", label: "Gemini 3.1 Pro + Nano Banana (premium)" },
  { value: "anthropic", label: "Claude Sonnet (premium)" },
];

export default function Dashboard() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState(null);
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", genre: "Auto", extra_ideas: "", provider: "openai", creation_mode: "video_inspired", story_prompt: "" });

  const load = async () => {
    try {
      const { data } = await api.get("/projects");
      setProjects(data);
    } catch (e) {
      toast.error("Failed to load projects");
      setProjects([]);
    }
  };

  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.title.trim()) return toast.error("Give your project a title");
    setCreating(true);
    try {
      const { data } = await api.post("/projects", form);
      toast.success("Project created");
      setOpen(false);
      navigate(`/studio/${data.id}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setCreating(false);
    }
  };

  const remove = async (id, e) => {
    e.stopPropagation();
    try {
      await api.delete(`/projects/${id}`);
      setProjects((p) => p.filter((x) => x.id !== id));
      toast.success("Project deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <TopNav />
      <main className="max-w-7xl mx-auto px-6 py-10">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
          <div>
            <h1 className="font-display text-4xl font-semibold tracking-tight">Your Studio</h1>
            <p className="text-white/50 font-light mt-2">Transform any video into a viral 3D masterpiece.</p>
          </div>
          <div className="flex items-center gap-3">
            <MentionInbox />
            <BrandKitDialog />
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button className="rounded-full h-11 px-6 bg-[#00E5FF] text-black hover:bg-[#00E5FF]/90 font-medium glow-cyan"
                  data-testid="new-project-btn">
                  <Plus className="w-4 h-4 mr-1" /> New project
                </Button>
              </DialogTrigger>
            <DialogContent className="bg-zinc-950 border-white/10 text-white sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="font-display text-2xl tracking-tight">New video project</DialogTitle>
                <DialogDescription className="text-white/50">Describe your video and let AI transform it into viral 3D content.</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-2">
                <div data-testid="story-templates">
                  <Label className="text-white/70 text-sm">Start from a template (optional)</Label>
                  <div className="flex flex-wrap gap-1.5 mt-1.5">
                    {STORY_TEMPLATES.map((t) => (
                      <button
                        key={t.key}
                        type="button"
                        data-testid={`story-template-${t.key}`}
                        onClick={() => setForm({ ...form, title: t.title, description: t.description, story_prompt: t.story_prompt, genre: t.genre })}
                        className="rounded-full border border-white/15 bg-black px-3 py-1 text-[11px] text-white/70 hover:border-[#00E5FF] hover:text-white transition-colors"
                      >
                        {t.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <Label className="text-white/70 text-sm">Title</Label>
                  <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                    className="mt-1.5 bg-black border-white/15 focus-visible:ring-[#00E5FF]"
                    placeholder="My epic clip" data-testid="project-title-input" />
                </div>
                <div>
                  <Label className="text-white/70 text-sm">What's it about?</Label>
                  <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                    className="mt-1.5 bg-black border-white/15 focus-visible:ring-[#00E5FF] min-h-[80px]"
                    placeholder="Describe the story, characters or moment…" data-testid="project-desc-input" />
                </div>
                <div data-testid="story-prompt-field">
                  <Label className="text-white/70 text-sm">Story, characters & moments — extra detail (optional)</Label>
                  <Textarea value={form.story_prompt} onChange={(e) => setForm({ ...form, story_prompt: e.target.value })}
                    className="mt-1.5 bg-black border-white/15 focus-visible:ring-[#00E5FF] min-h-[100px]"
                    placeholder="e.g. A young inventor in a neon city races to finish her flying machine before dawn…" data-testid="project-story-prompt-input" />
                  <p className="text-[11px] text-white/40 mt-1">You can upload a video later in the studio (optional) — or generate a full cinematic film straight from this story.</p>
                </div>
                <div>
                  <Label className="text-white/70 text-sm">Genre</Label>
                  <Select value={form.genre} onValueChange={(v) => setForm({ ...form, genre: v })}>
                    <SelectTrigger className="mt-1.5 bg-black border-white/15" data-testid="project-genre-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-950 border-white/10 text-white">
                      {GENRES.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-white/70 text-sm">AI engine</Label>
                  <Select value={form.provider} onValueChange={(v) => setForm({ ...form, provider: v })}>
                    <SelectTrigger className="mt-1.5 bg-black border-white/15" data-testid="project-engine-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-950 border-white/10 text-white">
                      {ENGINES.map((e) => <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button onClick={create} disabled={creating}
                  className="w-full rounded-full bg-[#00E5FF] text-black hover:bg-[#00E5FF]/90 font-medium"
                  data-testid="create-project-confirm-btn">
                  {creating ? "Creating…" : "Create & open studio"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        {projects === null ? (
          <div className="flex justify-center py-20">
            <div className="w-8 h-8 rounded-full border-2 border-[#00E5FF] border-t-transparent animate-spin" />
          </div>
        ) : projects.length === 0 ? (
          <div className="border border-dashed border-white/15 rounded-xl py-24 text-center" data-testid="empty-state">
            <Sparkles className="w-10 h-10 text-[#00E5FF] mx-auto mb-4" />
            <p className="font-display text-xl mb-1">No projects yet</p>
            <p className="text-white/50 font-light">Create your first project to start transforming.</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {projects.map((p, i) => (
              <motion.div key={p.id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: i * 0.04 }}
                onClick={() => navigate(`/studio/${p.id}`)}
                className="group cursor-pointer bg-zinc-950 border border-white/10 rounded-lg p-5 hover:-translate-y-1 hover:border-[#00E5FF]/40 transition-[transform,border-color] duration-300"
                data-testid={`project-card-${p.id}`}>
                <div className="flex items-start justify-between mb-4">
                  <div className="w-11 h-11 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center">
                    <Film className="w-5 h-5 text-[#00E5FF]" />
                  </div>
                  <button onClick={(e) => remove(p.id, e)}
                    className="opacity-0 group-hover:opacity-100 transition-opacity text-white/40 hover:text-[#FF007F]"
                    data-testid={`delete-project-${p.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <h3 className="font-display text-lg font-semibold tracking-tight mb-1 truncate">{p.title}</h3>
                <p className="text-sm text-white/50 font-light line-clamp-2 min-h-[40px]">
                  {p.description || "No description"}
                </p>
                <div className="flex items-center justify-between mt-4">
                  <span className={`text-xs px-2.5 py-1 rounded-full border capitalize ${STATUS_COLORS[p.status] || STATUS_COLORS.draft}`}>
                    {p.status}
                  </span>
                  <span className="text-xs text-white/30 flex items-center gap-1">
                    <Clock className="w-3 h-3" /> {new Date(p.created_at).toLocaleDateString()}
                  </span>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
